/**
 * OnboardingContext — single source of truth for the v2 onboarding state
 * (#1256 walkthrough).
 *
 * Fetches `/auth/onboarding` once per user/session and exposes:
 *   - `status` — the merged response (legacy + v2 fields)
 *   - `loading` — true until the first fetch resolves
 *   - `applyLocalUpdate(partial)` — optimistic-merge helper used by callers
 *     that just POSTed to /auth/onboarding/state
 *   - `postState(partial)` — single write path; returns the merged state
 *     and pre-applies it locally
 *   - `refresh()` — re-fetches from the server (rare; most updates flow
 *     through the optimistic helper)
 *
 * Wrap the app inside <Router> so navigation works in consumers (the tour
 * coachmark may navigate via the welcome scenario id).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { safeAuthFetch } from '../utils/safeFetch';
import { posthog } from '../main';

const OnboardingContext = createContext(null);

export function useOnboarding() {
  return useContext(OnboardingContext);
}

export function OnboardingProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingWelcome, setStartingWelcome] = useState(false);
  const fetchTokenRef = useRef(0);

  const fetchOnboarding = useCallback(async ({ background = false } = {}) => {
    // Token-based cancellation: a stale fetch resolving after a newer one
    // shouldn't overwrite the newer response.
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    // #3671: `loading` means "no status yet". A background refresh keeps the
    // current status on screen, so it must NOT flip `loading` — DashboardPageV2
    // unmounts the checklist row + CoachCard while loading, and ChecklistTile
    // refreshes on mount, which made an unmount/refresh/remount loop (~4/s).
    if (!background) setLoading(true);
    try {
      const response = await safeAuthFetch('/auth/onboarding');
      const data = await response.json();
      if (myToken === fetchTokenRef.current) setStatus(data);
    } catch (err) {
      console.error('[Onboarding] fetch failed:', err);
      // Fail-open: leave status null. Consumers default to "not v2 enabled".
      // Route to Sentry so a spike is visible — status:null silently disables
      // the welcome modal + checklist + tour for the whole session.
      Sentry.captureException(err, {
        tags: { errorId: 'ONBOARDING_STATUS_FETCH_FAILED_CLIENT' },
      });
    } finally {
      if (myToken === fetchTokenRef.current) setLoading(false);
    }
  }, []);

  // Stable, arg-less refresh for consumers: re-fetches without touching `loading`.
  const refresh = useCallback(() => fetchOnboarding({ background: true }), [fetchOnboarding]);

  // Fetch on auth changes. Skip when not authenticated or not a learner —
  // the tour/checklist surfaces only learners.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    if (user.role !== 'learner') {
      // Facilitators / admins still consume the onboarding endpoint via
      // LearnerDetailPage; they don't need their OWN status.
      setStatus(null);
      setLoading(false);
      return;
    }
    if (user.is_guest) {
      // Issue #1875: guests carry role 'learner' but /auth/onboarding is not on
      // GUEST_ALLOWED_ROUTES, so this fetch can only ever 403
      // (DEMO_SCOPE_RESTRICTED). It used to fire on every guest page-load and
      // was ~390 Sentry events/week on its own — 97% of the 2026-07-25 weekly
      // error spike — while the fail-open path landed on exactly this state.
      // The tour/checklist/welcome-modal are learner-account features, so
      // status:null is the correct answer for a guest, not a degradation.
      setStatus(null);
      setLoading(false);
      return;
    }
    fetchOnboarding();
  }, [isAuthenticated, user, fetchOnboarding]);

  // Optimistic local merge — used by the welcome modal, checklist tile,
  // and tour engine after a successful POST. Avoids a refetch round-trip
  // and keeps the UI responsive. Recomputes the derived flags
  // (show_welcome_modal, checklist.tour_complete, checklist.dismissed)
  // so a stale top-level flag doesn't re-fire the modal/tour on the
  // next render — without this, the modal reappears after dismissal
  // because show_welcome_modal stays true in cache (#1258 bug).
  //
  // Two call shapes reach here (review-fix #2413 P1.2 — both must stay
  // correct):
  //   1. TourProvider's `writeState` (via App.jsx's `onLocalStateChange`)
  //      — a genuine small PARTIAL of raw top-level fields
  //      (tour_step/tour_phase/tour_dismissed/welcome_run_id/
  //      welcome_modal_dismissed), fired BEFORE the matching POST resolves.
  //      It never carries a `checklist` key.
  //   2. DashboardPageV2's `applyOnboardingStateUpdate` wrapper — called
  //      AFTER `postState` has already replaced `status` with the FULL
  //      authoritative `_buildOnboardingResponse` shape (its own comment
  //      calls this "a no-op now"). `partial` here IS that full response,
  //      so it DOES carry an authoritative `checklist` (including
  //      `never_escorted`, #2413).
  //
  // The old unconditional recompute below was wrong for both: (a) it
  // derived `tour_complete` from `tour_phase`/`tour_dismissed` alone,
  // with no knowledge of `never_escorted` (#2413) — for the migration-
  // backfilled cohort the server deliberately sends `tour_complete:false`
  // (stored `tour_phase:'complete'` is a migration artifact, not a real
  // finish), and shape 2 above reverted that back to `true` in-session,
  // rendering "✓ Take a quick tour" AND the new "Take the tour →" button
  // at once; (b) it read `merged.checklist_dismissed`, a field that does
  // not exist ANYWHERE in either shape (the real value lives at
  // `checklist.dismissed`, nested) — `undefined === true` is always
  // `false`, so every call through shape 2 silently downgraded `dismissed`
  // back to `false`, e.g. immediately reverting a learner's own dismiss
  // click that `handleChecklistDismiss` had just gotten a correct
  // `dismissed:true` back for via `postState`.
  //
  // Fix: when `partial` carries its own `checklist` (shape 2), it is
  // already authoritative — merge it through as-is rather than
  // recomputing. Only fall back to the raw-field recomputation for a
  // genuine partial (shape 1), and even then preserve `never_escorted`
  // from the last authoritative fetch so `tour_complete` can't flip back
  // true for the backfilled cohort mid-tour-write.
  const applyLocalUpdate = useCallback((partial) => {
    setStatus((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...partial };
      const v2 = merged.onboarding_v2_enabled === true;
      const hasScenario = merged.welcome_scenario_id !== null
        && merged.welcome_scenario_id !== undefined;
      merged.show_welcome_modal = v2
        && !merged.welcome_modal_dismissed
        && hasScenario;
      if (partial && partial.checklist) {
        // Shape 2 — a full authoritative response already computed
        // tour_complete/dismissed/never_escorted server-side. Trust it.
        merged.checklist = { ...(prev.checklist || {}), ...partial.checklist };
      } else {
        const neverEscorted = prev.checklist?.never_escorted === true;
        const rawTourComplete = merged.tour_phase === 'complete'
          || merged.tour_dismissed === true;
        merged.checklist = {
          ...(prev.checklist || {}),
          tour_complete: neverEscorted ? false : rawTourComplete,
          // dismissed / scenario_complete / voice_used / builder_used /
          // never_escorted require a server round-trip; leave as-is on a
          // raw-field optimistic merge. Next /auth/onboarding refresh
          // (e.g. on next login, or the authoritative postState response
          // that follows this optimistic write) refreshes them.
        };
      }
      return merged;
    });
  }, []);

  const postState = useCallback(
    async (partial) => {
      const response = await safeAuthFetch('/auth/onboarding/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partial),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.error || `onboarding/state failed (${response.status})`);
      }
      const data = await response.json();
      // Authoritative refresh from the server's full _buildOnboardingResponse
      // shape — includes derived checklist fields the local optimistic merge
      // can't compute (scenario_complete, voice_used, builder_used).
      if (data?.status) setStatus(data.status);
      return data.status;
    },
    []
  );

  // Unified action — used by welcome modal, tour coachmark step 1, and
  // welcome scenario card click. Without this, the three call sites
  // each implemented different subsets of the "start the welcome
  // scenario + advance tour" transition and silently fell out of sync
  // (e.g. tour advance without scenario start strands the learner on
  // dashboard mid-tour; card click bypassed tour state entirely).
  //
  // TWO MODES (#1378 — do NOT collapse back to a single atomic write):
  //
  //   tour-carrying start (tourActive: tour not dismissed and not
  //   complete) — the genuine first-time OR restarted tour:
  //     1. POST /api/runs to create the welcome scenario run
  //     2. POST /auth/onboarding/state with welcome_modal_dismissed=true,
  //        welcome_run_id=<newRunId>, tour_step=2, tour_phase='mid_scenario'
  //     3. Navigate to /chat/:runId
  //
  //   plain replay start (!tourActive: tour dismissed or complete) — a
  //   returning learner re-running the optional intro:
  //     1. POST /api/runs to create the welcome scenario run
  //     2. (no onboarding-state write)
  //     3. Navigate to /chat/:runId
  //
  // Why the plain-replay branch must NOT write state: the prior code
  // unconditionally overwrote welcome_run_id with each fresh (incomplete)
  // run, perpetually resetting the derived checklist.scenario_complete to
  // false → the dashboard kept re-promoting the welcome card with a
  // "0 attempts" override → learners (user 599 / 56 abandons) re-clicked
  // into a silent loop. Phase 2 cannot fire for a dismissed/complete tour
  // anyway (TourProvider.triggerPhase2 early-returns on tourDismissed ||
  // tourCompleted), so suppressing the write there is behaviour-preserving.
  // The predicate is tour-INTENT only — it must NOT include
  // scenario_complete: restartTour() does not clear welcome_run_id, so a
  // completed learner who restarts the tour still has scenario_complete
  // true but is genuinely tour-active and needs the rebinding write.
  //
  // Errors at the run-create step are surfaced (no point continuing).
  // Errors at the state-write step are non-blocking — the run is real,
  // the learner should still land in it.
  const startWelcomeScenario = useCallback(
    async ({ source } = {}) => {
      if (startingWelcome) return;
      const scenarioId = status?.welcome_scenario_id;
      if (!scenarioId) {
        console.error('[Onboarding] startWelcomeScenario: no welcome_scenario_id');
        return;
      }
      setStartingWelcome(true);
      if (source) {
        posthog?.capture?.('onboarding_welcome_scenario_started', { source });
      }
      try {
        const response = await safeAuthFetch('/api/runs?skipOpening=true', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scenarioId }),
        });
        if (!response.ok) throw new Error(`run create failed (${response.status})`);
        const runData = await response.json();
        const newRunId = runData.run.id;

        // #1378: two modes. Tour-INTENT only — deliberately excludes the
        // completed-run signal. restartTour() leaves welcome_run_id set, so
        // a completed learner restarting the tour is still tour-active and
        // needs the rebinding write (Codex P1). See TWO MODES docstring.
        const tourActive =
          status?.tour_dismissed !== true &&
          status?.tour_phase !== 'complete';

        if (tourActive) {
          // Phase-2 trigger needs welcome_run_id keyed at this moment.
          // Tour transition to mid_scenario happens here too so the
          // dashboard tour overlay unmounts cleanly before navigation.
          try {
            await postState({
              welcome_modal_dismissed: true,
              welcome_run_id: newRunId,
              tour_step: 2,
              tour_phase: 'mid_scenario',
            });
          } catch (stateErr) {
            // Non-blocking — the run is real; we still navigate. But this
            // write IS load-bearing for tour continuity: without welcome_run_id
            // on the server, phase 2 never fires inside AssessmentResults and
            // the tour silently stalls. Route to Sentry so a spike is visible.
            console.warn('[Onboarding] welcome_run_id write failed:', stateErr);
            Sentry.captureException(stateErr, {
              tags: { errorId: 'ONBOARDING_WELCOME_RUN_ID_WRITE_FAILED' },
              extra: { runId: newRunId, source },
            });
          }
        }
        // !tourActive (plain replay): intentionally NO postState — see the
        // TWO MODES docstring above. Writing here is what caused #1378.

        // Opening is generated on-demand at text-mode selection in ChatPage
        // (#1780: this used to preflight-fire /opening here, before the
        // learner chose text vs voice, wasting a Gemini call for every
        // voice/abandoned run).

        navigate(`/chat/${newRunId}`, { state: { from: 'onboarding_v2' } });
      } catch (err) {
        // Outer-catch covers run-create failure + any uncaught error in the
        // unified action. Before #1315, this was console.error only — meaning
        // a learner who clicked the welcome card during a network blip got a
        // dead button with no server-side trace. Surface to Sentry and emit
        // a structured warn line so the next "why didn't my checklist tick?"
        // debug takes 30 seconds, not an hour (#1315 acceptance criterion).
        console.warn('[Onboarding] startWelcomeScenario failed:', {
          errorId: 'ONBOARDING_WELCOME_SCENARIO_START_FAILED',
          source,
          message: err?.message,
        });
        Sentry.captureException(err, {
          tags: { errorId: 'ONBOARDING_WELCOME_SCENARIO_START_FAILED' },
          extra: { source },
        });
      } finally {
        setStartingWelcome(false);
      }
    },
    // Depend on the whole `status` object, not individual leaves: the body
    // reads status.tour_dismissed / tour_phase / welcome_scenario_id, and
    // postState() replaces `status` wholesale with the server response
    // (subsystem guide: "Frontend replaces local status with the server
    // response"). Pinning primitive leaves let the callback keep a stale
    // `status` closure when a sibling field changed — review P1 (#1378).
    [status, startingWelcome, postState, navigate]
  );

  const value = {
    status,
    loading,
    startingWelcome,
    applyLocalUpdate,
    postState,
    refresh,
    startWelcomeScenario,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export default OnboardingContext;
